import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import YAML from "yaml";
import Header from "../../components/Header";
import saasApi from "../../utils/saasApi";
import {
  formatRuleValue,
  ownShopliftingRules,
  shopliftingRuleErrors,
  visibleShopliftingRules,
} from "./shopliftingRules";

const cloneJson = (value) => JSON.parse(JSON.stringify(value ?? {}));
const pretty = (value) => JSON.stringify(value ?? {}, null, 2);
const isPlainObject = (value) => value && typeof value === "object" && !Array.isArray(value);
const COMMON_FIELDS = new Set(["id", "name", "rtsp_url", "fps", "detections"]);

const camerasFrom = (configuration) => {
  if (Array.isArray(configuration)) return configuration;
  if (Array.isArray(configuration?.cameras)) return configuration.cameras;
  return [];
};

const setCamerasOn = (configuration, cameras) => {
  if (Array.isArray(configuration)) return cameras;
  return { ...(configuration || {}), cameras };
};

const ShopliftingRulesEditor = ({ camera, globalRules, onChange, onReset }) => {
  const own = ownShopliftingRules(camera);
  const ownCount = Object.keys(own).length;
  const errors = shopliftingRuleErrors(camera, globalRules);

  return (
    <Box sx={{ border: 1, borderColor: "divider", borderRadius: "8px", p: 1.5 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1} sx={{ mb: 1 }}>
        <Typography variant="subtitle2">Reglas del hurto en esta camara</Typography>
        <Chip
          size="small"
          color={ownCount ? "primary" : "default"}
          label={ownCount ? `${ownCount} regla${ownCount === 1 ? "" : "s"} propia${ownCount === 1 ? "" : "s"}` : "Usa las globales"}
        />
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Campo vacio = usa el valor global de Detecciones. Lo que se cargue aca vale solo para esta camara.
      </Typography>
      {errors.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errors.map((error) => <div key={error}>{error}</div>)}
        </Alert>
      )}
      <Stack spacing={2}>
        {visibleShopliftingRules(camera, globalRules).map(({ key, label, type, min, max, help, options }) => {
          const hasOwn = key in own;
          const globalText = `Global: ${formatRuleValue(globalRules?.[key])}`;
          const helperText = [hasOwn ? `Propia de esta camara. ${globalText}` : globalText, help].filter(Boolean).join(" · ");
          if (type === "choice") {
            const current = hasOwn ? own[key] : globalRules?.[key];
            const known = options.some((option) => option.value === own[key]);
            return (
              <TextField
                key={key}
                select
                label={label}
                value={hasOwn ? own[key] : ""}
                onChange={(event) => onChange(key, event.target.value === "" ? undefined : event.target.value)}
                helperText={[helperText, options.find((option) => option.value === current)?.help].filter(Boolean).join(" · ")}
                SelectProps={{ displayEmpty: true }}
                InputLabelProps={{ shrink: true }}
                fullWidth
              >
                <MenuItem value="">Usar la global ({formatRuleValue(globalRules?.[key])})</MenuItem>
                {options.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
                {hasOwn && !known && <MenuItem value={own[key]}>{String(own[key])} (no valido)</MenuItem>}
              </TextField>
            );
          }
          if (type === "bool") {
            return (
              <TextField
                key={key}
                select
                label={label}
                value={hasOwn ? String(own[key]) : ""}
                onChange={(event) => onChange(key, event.target.value === "" ? undefined : event.target.value === "true")}
                helperText={helperText}
                SelectProps={{ displayEmpty: true }}
                InputLabelProps={{ shrink: true }}
                fullWidth
              >
                <MenuItem value="">Usar la global ({formatRuleValue(globalRules?.[key])})</MenuItem>
                <MenuItem value="true">Si</MenuItem>
                <MenuItem value="false">No</MenuItem>
              </TextField>
            );
          }
          return (
            <TextField
              key={key}
              label={label}
              type="number"
              value={hasOwn ? own[key] : ""}
              placeholder={formatRuleValue(globalRules?.[key])}
              onChange={(event) => {
                const raw = event.target.value;
                if (raw === "") {
                  onChange(key, undefined);
                  return;
                }
                const parsed = Number(raw);
                if (Number.isFinite(parsed)) onChange(key, parsed);
              }}
              inputProps={{ step: type === "int" ? 1 : 0.01, min, max: max ?? undefined }}
              InputLabelProps={{ shrink: true }}
              helperText={helperText}
              fullWidth
            />
          );
        })}
      </Stack>
      <Button variant="outlined" color="warning" onClick={onReset} disabled={!ownCount} sx={{ mt: 2 }}>
        Volver a las globales
      </Button>
    </Box>
  );
};

const JsonValueEditor = ({ label, value, onCommit }) => {
  const [text, setText] = useState(pretty(value));
  const [error, setError] = useState("");

  useEffect(() => {
    setText(pretty(value));
    setError("");
  }, [value]);

  const commit = () => {
    try {
      onCommit(JSON.parse(text));
      setError("");
    } catch (err) {
      setError(err.message || "JSON invalido");
    }
  };

  return (
    <TextField
      label={label}
      value={text}
      onChange={(event) => setText(event.target.value)}
      onBlur={commit}
      multiline
      minRows={4}
      fullWidth
      error={Boolean(error)}
      helperText={error || "JSON: se aplica al salir del campo"}
      spellCheck={false}
    />
  );
};

const CameraExtraFields = ({ camera, onChange }) => {
  // detector_params con solo las reglas del hurto se edita en su seccion; si trae otros detectores, se muestra entero.
  const onlyShoplifting = (value) => isPlainObject(value) && Object.keys(value).every((key) => key === "shoplifting");
  const extras = Object.entries(camera || {}).filter(([key, value]) => (
    !COMMON_FIELDS.has(key) && !(key === "detector_params" && onlyShoplifting(value))
  ));
  if (!extras.length) return <Alert severity="info">No hay campos adicionales en esta camara.</Alert>;

  return (
    <Stack spacing={2}>
      {extras.map(([key, value]) => {
        if (typeof value === "boolean") {
          return (
            <TextField
              key={key}
              select
              label={key}
              value={String(value)}
              onChange={(event) => onChange(key, event.target.value === "true")}
              fullWidth
            >
              <MenuItem value="true">true</MenuItem>
              <MenuItem value="false">false</MenuItem>
            </TextField>
          );
        }
        if (typeof value === "number") {
          return (
            <TextField
              key={key}
              label={key}
              type="number"
              value={value}
              onChange={(event) => {
                const parsed = Number(event.target.value);
                if (Number.isFinite(parsed)) onChange(key, parsed);
              }}
              fullWidth
            />
          );
        }
        if (typeof value === "string") {
          return (
            <TextField key={key} label={key} value={value} onChange={(event) => onChange(key, event.target.value)} fullWidth />
          );
        }
        return <JsonValueEditor key={key} label={key} value={value} onCommit={(next) => onChange(key, next)} />;
      })}
    </Stack>
  );
};

export default function CamerasYamlEditor() {
  const [original, setOriginal] = useState(null);
  const [draft, setDraft] = useState(null);
  const [detectorNames, setDetectorNames] = useState([]);
  const [shopliftingGlobal, setShopliftingGlobal] = useState({});
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [tab, setTab] = useState("visual");
  const [manualFormat, setManualFormat] = useState("json");
  const [manualText, setManualText] = useState("");
  const [manualError, setManualError] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState({ type: "", msg: "" });

  const cameras = useMemo(() => camerasFrom(draft), [draft]);
  const selectedCamera = cameras[selectedIdx] || null;
  // Se muestra si la camara tiene hurto, o si conserva reglas propias aunque se lo hayan sacado.
  const showShopliftingRules = Boolean(selectedCamera) && (
    (Array.isArray(selectedCamera.detections) && selectedCamera.detections.includes("shoplifting"))
    || Object.keys(ownShopliftingRules(selectedCamera)).length > 0
  );
  const dirty = useMemo(() => Boolean(original && draft && pretty(original) !== pretty(draft)), [original, draft]);

  const syncManualText = useCallback((configuration = draft, format = manualFormat) => {
    if (!configuration) return;
    setManualText(format === "yaml" ? YAML.stringify(configuration) : pretty(configuration));
    setManualError("");
  }, [draft, manualFormat]);

  const load = useCallback(async () => {
    setLoading(true);
    setStatus({ type: "", msg: "" });
    try {
      const [cameraConfig, detectionsConfig] = await Promise.all([
        saasApi.getInternalCamerasConfig(),
        saasApi.getInternalDetectionsConfig().catch(() => ({ detectors: {} })),
      ]);
      const next = cloneJson(cameraConfig);
      const names = Object.keys(detectionsConfig?.detectors || {}).sort();
      const globalShoplifting = detectionsConfig?.detectors?.shoplifting ?? detectionsConfig?.shoplifting;
      setOriginal(next);
      setDraft(cloneJson(next));
      setDetectorNames(names);
      setShopliftingGlobal(isPlainObject(globalShoplifting) ? globalShoplifting : {});
      setSelectedIdx(0);
      setManualText(manualFormat === "yaml" ? YAML.stringify(next) : pretty(next));
    } catch (err) {
      setStatus({ type: "error", msg: err.message || "No se pudo cargar cameras.json" });
    } finally {
      setLoading(false);
    }
  }, [manualFormat]);

  useEffect(() => {
    load();
  }, [load]);

  const updateCamera = (idx, patch) => {
    setDraft((current) => setCamerasOn(
      current,
      camerasFrom(current).map((camera, i) => (i === idx ? { ...camera, ...patch } : camera))
    ));
  };

  const updateCameraField = (idx, key, value) => {
    updateCamera(idx, { [key]: value });
  };

  // value undefined = la camara deja de definir esa regla y vuelve a la global.
  // Sin reglas propias se borra detector_params.shoplifting, y detector_params si queda vacio.
  const setOwnShopliftingRules = (idx, buildRules) => {
    setDraft((current) => setCamerasOn(
      current,
      camerasFrom(current).map((camera, i) => {
        if (i !== idx) return camera;
        const params = isPlainObject(camera.detector_params) ? { ...camera.detector_params } : {};
        const rules = buildRules({ ...ownShopliftingRules(camera) });
        if (Object.keys(rules).length) params.shoplifting = rules;
        else delete params.shoplifting;
        const nextCamera = { ...camera };
        if (Object.keys(params).length) nextCamera.detector_params = params;
        else delete nextCamera.detector_params;
        return nextCamera;
      })
    ));
  };

  const updateShopliftingRule = (idx, key, value) => {
    setOwnShopliftingRules(idx, (rules) => {
      if (value === undefined) delete rules[key];
      else rules[key] = value;
      return rules;
    });
  };

  const addCamera = () => {
    setDraft((current) => {
      const list = camerasFrom(current);
      const nextCamera = {
        id: list.length ? Math.max(...list.map((camera) => Number(camera.id)).filter(Number.isFinite), 0) + 1 : 1,
        name: `Camera-${list.length + 1}`,
        rtsp_url: "",
        detections: [],
      };
      setSelectedIdx(list.length);
      return setCamerasOn(current, [...list, nextCamera]);
    });
  };

  const deleteCamera = (idx) => {
    setDraft((current) => {
      const nextList = camerasFrom(current).filter((_, cameraIdx) => cameraIdx !== idx);
      setSelectedIdx((currentIdx) => Math.min(currentIdx, Math.max(nextList.length - 1, 0)));
      return setCamerasOn(current, nextList);
    });
  };

  const parseManual = () => {
    try {
      const parsed = manualFormat === "yaml" ? YAML.parse(manualText) : JSON.parse(manualText);
      if (!isPlainObject(parsed) && !Array.isArray(parsed)) throw new Error("La configuracion debe ser un objeto o array JSON");
      if (!Array.isArray(parsed) && parsed.cameras !== undefined && !Array.isArray(parsed.cameras)) {
        throw new Error("El campo cameras debe ser un array");
      }
      setDraft(parsed);
      setSelectedIdx(0);
      setManualError("");
      setStatus({ type: "success", msg: "Editor manual aplicado al borrador. Revisa y guarda para enviar al motor." });
    } catch (err) {
      setManualError(err.message || "No se pudo parsear el contenido manual");
    }
  };

  const validateBeforeSave = () => {
    if (!isPlainObject(draft) && !Array.isArray(draft)) return "La configuracion debe ser un objeto JSON o un array de camaras";
    if (!Array.isArray(draft) && !Array.isArray(draft.cameras)) return "La configuracion debe incluir cameras como array";
    const invalid = camerasFrom(draft).find((camera) => camera.detections !== undefined && !Array.isArray(camera.detections));
    if (invalid) return `La camara ${invalid.name || invalid.id || ""} tiene detections invalido`;
    for (const camera of camerasFrom(draft)) {
      const errors = shopliftingRuleErrors(camera, shopliftingGlobal);
      if (errors.length) return `Camara ${camera.name || camera.id || ""}: ${errors[0]}`;
    }
    return "";
  };

  const save = async () => {
    const validationError = validateBeforeSave();
    if (validationError) {
      setStatus({ type: "error", msg: validationError });
      return;
    }
    setSaving(true);
    setStatus({ type: "", msg: "" });
    try {
      const saved = await saasApi.saveInternalCamerasConfig(draft);
      const next = cloneJson(saved);
      setOriginal(next);
      setDraft(cloneJson(next));
      setSelectedIdx((idx) => Math.min(idx, Math.max(camerasFrom(next).length - 1, 0)));
      syncManualText(next);
      setStatus({ type: "success", msg: "Configuracion de camaras guardada. El motor la recarga sin reiniciarse." });
    } catch (err) {
      setStatus({ type: "error", msg: err.message || "No se pudo guardar cameras.json" });
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => {
    const next = cloneJson(original);
    setDraft(next);
    syncManualText(next);
    setStatus({ type: "success", msg: "Cambios descartados" });
  };

  return (
    <Box m="20px">
      <Header title="CAMARAS DEL MOTOR" subtitle="Configuracion real desde cameras.json y detectores habilitados por camara" />

      {loading && <LinearProgress sx={{ mb: 2 }} />}
      {status.msg && <Alert severity={status.type === "error" ? "error" : "success"} sx={{ mb: 2 }}>{status.msg}</Alert>}

      <Stack direction="row" gap={1} flexWrap="wrap" sx={{ mb: 2 }}>
        <Button variant="outlined" onClick={load} disabled={loading || saving}>Recargar</Button>
        <Button variant="outlined" onClick={cancel} disabled={!dirty || loading || saving}>Cancelar cambios</Button>
        <Button variant="contained" onClick={save} disabled={!dirty || loading || saving}>Guardar cambios</Button>
        {dirty && <Chip color="warning" label="Cambios sin guardar" />}
      </Stack>

      {!draft ? (
        <Alert severity="info">Cargando configuracion...</Alert>
      ) : (
        <>
          <Tabs value={tab} onChange={(_, value) => {
            setTab(value);
            if (value === "manual") syncManualText();
          }} sx={{ mb: 2 }}>
            <Tab value="visual" label="Editor interactivo" />
            <Tab value="manual" label="Editor manual YAML/JSON" />
            <Tab value="full" label="Documento completo" />
          </Tabs>

          {tab === "visual" && (
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <Card sx={{ borderRadius: "8px" }}>
                  <CardContent>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1} sx={{ mb: 1 }}>
                      <Typography variant="h6">Camaras</Typography>
                      <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={addCamera}>Agregar</Button>
                    </Stack>
                    <Stack direction="row" gap={1} flexWrap="wrap">
                      {cameras.map((camera, idx) => (
                        <Chip
                          key={`${camera.id || camera.name || idx}-${idx}`}
                          label={camera.name || camera.id || `Camara ${idx + 1}`}
                          color={idx === selectedIdx ? "primary" : "default"}
                          onClick={() => setSelectedIdx(idx)}
                        />
                      ))}
                    </Stack>
                    {!cameras.length && <Alert severity="info" sx={{ mt: 2 }}>No hay camaras en cameras.json.</Alert>}
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="body2" color="text.secondary">
                      Se edita el archivo completo y se preservan campos no conocidos.
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={5}>
                <Card sx={{ borderRadius: "8px" }}>
                  <CardContent>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1} sx={{ mb: 2 }}>
                      <Typography variant="h6">
                        {selectedCamera ? `Editando ${selectedCamera.name || selectedCamera.id || selectedIdx + 1}` : "Camara"}
                      </Typography>
                      {selectedCamera && (
                        <IconButton size="small" color="error" onClick={() => deleteCamera(selectedIdx)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Stack>
                    {selectedCamera ? (
                      <Stack spacing={2}>
                        {"id" in selectedCamera && (
                          <TextField
                            label="id"
                            value={selectedCamera.id}
                            onChange={(event) => {
                              const raw = event.target.value;
                              const parsed = Number(raw);
                              updateCameraField(selectedIdx, "id", Number.isFinite(parsed) && raw !== "" ? parsed : raw);
                            }}
                            fullWidth
                          />
                        )}
                      <TextField
                          label="name"
                          value={selectedCamera.name || ""}
                          onChange={(event) => updateCameraField(selectedIdx, "name", event.target.value)}
                          fullWidth
                        />
                        {"rtsp_url" in selectedCamera && (
                          <TextField
                            label="rtsp_url"
                            value={selectedCamera.rtsp_url || ""}
                            onChange={(event) => updateCameraField(selectedIdx, "rtsp_url", event.target.value)}
                            fullWidth
                          />
                        )}
                        {"fps" in selectedCamera && (
                          <TextField
                            label="fps"
                            type="number"
                            value={selectedCamera.fps ?? ""}
                            onChange={(event) => {
                              const parsed = Number(event.target.value);
                              if (Number.isFinite(parsed)) updateCameraField(selectedIdx, "fps", parsed);
                            }}
                            fullWidth
                          />
                        )}
                        <TextField
                          select
                          label="detections"
                          value={Array.isArray(selectedCamera.detections) ? selectedCamera.detections : []}
                          onChange={(event) => {
                            const value = event.target.value;
                            updateCameraField(selectedIdx, "detections", typeof value === "string" ? value.split(",") : value);
                          }}
                          SelectProps={{ multiple: true }}
                          helperText="Detectores habilitados para esta camara. Esto pertenece a cameras.json."
                          fullWidth
                        >
                          {detectorNames.map((name) => (
                            <MenuItem key={name} value={name}>{name}</MenuItem>
                          ))}
                        </TextField>

                        {showShopliftingRules && (
                          <ShopliftingRulesEditor
                            camera={selectedCamera}
                            globalRules={shopliftingGlobal}
                            onChange={(key, value) => updateShopliftingRule(selectedIdx, key, value)}
                            onReset={() => setOwnShopliftingRules(selectedIdx, () => ({}))}
                          />
                        )}

                        <Typography variant="subtitle2">Campos adicionales preservados</Typography>
                        <CameraExtraFields
                          camera={selectedCamera}
                          onChange={(key, value) => updateCameraField(selectedIdx, key, value)}
                        />
                      </Stack>
                    ) : (
                      <Alert severity="info">Selecciona una camara.</Alert>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card sx={{ borderRadius: "8px", height: "100%" }}>
                  <CardContent>
                    <Typography variant="h6">Resumen</Typography>
                    <Typography variant="body2" color="text.secondary">{cameras.length} camaras configuradas.</Typography>
                    <Typography variant="body2" color="text.secondary">El PUT envia cameras.json completo.</Typography>
                    <Box
                      component="pre"
                      sx={{
                        mt: 2,
                        p: 1.5,
                        borderRadius: "8px",
                        border: (theme) => `1px solid ${theme.palette.divider}`,
                        bgcolor: (theme) => theme.custom.surface.base,
                        color: "text.primary",
                        fontSize: 12,
                        overflow: "auto",
                        maxHeight: "62vh",
                        fontFamily: (theme) => theme.custom.monoFont,
                      }}
                    >
                      {pretty(draft)}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}

          {tab === "manual" && (
            <Card sx={{ borderRadius: "8px" }}>
              <CardContent>
                <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center" sx={{ mb: 2 }}>
                  <TextField select size="small" label="Formato" value={manualFormat} onChange={(event) => {
                    const nextFormat = event.target.value;
                    setManualFormat(nextFormat);
                    syncManualText(draft, nextFormat);
                  }} sx={{ minWidth: 160 }}>
                    <MenuItem value="json">JSON</MenuItem>
                    <MenuItem value="yaml">YAML</MenuItem>
                  </TextField>
                  <Button variant="outlined" onClick={parseManual}>Aplicar al borrador</Button>
                  <Alert severity="info" sx={{ py: 0 }}>El guardado envia el documento completo de cameras.json al backend.</Alert>
                </Stack>
                {manualError && <Alert severity="error" sx={{ mb: 2 }}>{manualError}</Alert>}
                <TextField
                  value={manualText}
                  onChange={(event) => setManualText(event.target.value)}
                  multiline
                  minRows={28}
                  fullWidth
                  spellCheck={false}
                  disabled={loading || saving}
                />
              </CardContent>
            </Card>
          )}

          {tab === "full" && (
            <Card sx={{ borderRadius: "8px" }}>
              <CardContent>
                <Typography variant="h6">Documento completo preservado</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Vista exacta del objeto que se enviara en el PUT.</Typography>
                <Box component="pre" sx={{ p: 1.5, borderRadius: "8px", border: (theme) => `1px solid ${theme.palette.divider}`, bgcolor: (theme) => theme.custom.surface.base, color: "text.primary", fontSize: 12, overflow: "auto", maxHeight: "72vh", fontFamily: (theme) => theme.custom.monoFont }}>
                  {pretty(draft)}
                </Box>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </Box>
  );
}
